import { HubFeature } from '@ats/api/cm';
import { LocDefs, LocPortal, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Login';

const sharedProps = {
	portal: 'CP' as LocPortal,
	feature: HubFeature.CandidateAccessLogin,
};

export type LoginLoc =
	| 'CPLoginNewCandidateRegister'
	| 'CPLoginMethodLabel'
	| 'CPLoginEmailLabel'
	| 'CPLoginUsernameLabel'
	| 'CPLoginUsernameOrEmailLabel'
	| 'CPLoginUsernameFieldError'
	| 'CPLoginEmailRequiredError'
	| 'CPLoginEmailInvalidError'
	| 'CPLoginUsernameorEmailRequiredError'
	| 'CPLoginPasswordLabel'
	| 'CPLoginPasswordFieldError'
	| 'CPLoginSubmitButton'
	| 'CPLoginForgotPasswordButton'
	| 'CPLoginRegisterButton'
	| 'CPLoginCustomMessage'
	| 'CPLoginCompanyLogo'
	| 'CPLoginPageTitle';

export const loginLocs: LocDefs<LoginLoc> = {
	CPLoginNewCandidateRegister: {
		defaultValue: 'New Candidate Register',
		description: 'Link to the registration page for new candidates',
		displayPath: `${dpRoot}|New Candidate Register`,
		displayType: 'Button',
		feature: HubFeature.CandidateAccessLoginSelfRegistration,
		portal: 'CP',
	},
	CPLoginMethodLabel: {
		defaultValue: 'Log in using',
		description: 'Label for the login method selection',
		displayPath: `${dpRoot}|Method Selection`,
		displayType: 'Label',
		feature: HubFeature.CandidateAccessLoginEmail,
		portal: 'CP',
		legacyName: 'CandidateAccess.Login.Email/_View.Login.FieldLabel.Method',
	},
	CPLoginEmailLabel: {
		defaultValue: 'Email',
		description: 'Email field label',
		displayPath: `${dpRoot}|Email`,
		displayType: 'Label',
		feature: HubFeature.CandidateAccessLoginEmail,
		portal: 'CP',
		legacyName: 'CandidateAccess.Login.Email/_View.Login.FieldLabel.Email',
	},
	CPLoginUsernameLabel: {
		defaultValue: 'Username',
		description: 'Label for the username input.',
		displayPath: `${dpRoot}|Username`,
		displayType: 'Label',
		legacyName: 'CandidateAccess.Login/_View.Login.FieldLabel.Username',
		...sharedProps,
	},
	CPLoginUsernameOrEmailLabel: {
		defaultValue: 'Username/Email',
		description: 'Label for the username input.',
		displayPath: `${dpRoot}|Username or Email`,
		displayType: 'Label',
		portal: 'CP' as LocPortal,
		feature: HubFeature.CandidateAccessLoginUsernameOrEmail,
	},
	CPLoginUsernameorEmailRequiredError: {
		defaultValue: 'Please enter your username/email',
		description: 'Error saying that the username/email is required.',
		displayPath: `${dpRoot}|Username/Email required Error`,
		displayType: 'Error',
		portal: 'CP' as LocPortal,
		feature: HubFeature.CandidateAccessLoginUsernameOrEmail,
	},
	CPLoginUsernameFieldError: {
		defaultValue: 'Please enter your username',
		description: 'Error saying that the username is required.',
		displayPath: `${dpRoot}|Username Error`,
		displayType: 'Error',
		legacyName: 'CandidateAccess.Login/_View.Login.FieldError.Username',
		...sharedProps,
	},
	CPLoginEmailRequiredError: {
		defaultValue: 'Please enter your email',
		description: 'Error message if email field is empty',
		displayPath: `${dpRoot}|Email Required`,
		displayType: 'Error',
		legacyName: 'CandidateAccess.Login.Email/_View.Login.FieldError.EmailRequired',
		...sharedProps,
	},
	CPLoginEmailInvalidError: {
		defaultValue: 'Please use a valid email address',
		description: 'Error message if the email address is invalid',
		displayPath: `${dpRoot}|Email Invalid`,
		displayType: 'Error',
		legacyName: 'CandidateAccess.Login.Email/_View.Login.FieldError.EmailInvalid',
		...sharedProps,
	},
	CPLoginPasswordLabel: {
		defaultValue: 'Password',
		description: 'Label for the password input.',
		displayPath: `${dpRoot}|Password`,
		displayType: 'Label',
		legacyName: 'CandidateAccess.Login/_View.Login.FieldLabel.Password',
		...sharedProps,
	},
	CPLoginPasswordFieldError: {
		defaultValue: 'Please enter your password',
		description: 'Error saying that the password is required.',
		displayPath: `${dpRoot}|Password Error`,
		displayType: 'Error',
		legacyName: 'CandidateAccess.Login/_View.Login.FieldError.Password',
		...sharedProps,
	},
	CPLoginSubmitButton: {
		defaultValue: 'Log In',
		description: 'Log in button.',
		displayPath: `${dpRoot}|Submit`,
		displayType: 'Button',
		legacyName: 'CandidateAccess.Login/_View.Login.Button.Submit',
		...sharedProps,
	},
	CPLoginForgotPasswordButton: {
		defaultValue: 'Forgot password?',
		description: 'Forgot password button.',
		displayPath: `${dpRoot}|Forgot Password`,
		displayType: 'Button',
		legacyName: 'CandidateAccess.Login/_View.Login.Button.ForgotPassword',
		...sharedProps,
	},
	CPLoginRegisterButton: {
		defaultValue: 'Never logged in before?',
		description: 'Button to take candidates to the first time login page.',
		displayPath: `${dpRoot}|Never Logged In`,
		displayType: 'Button',
		legacyName: 'CandidateAccess.Login/_View.Login.Button.Register',
		...sharedProps,
	},
	CPLoginCustomMessage: {
		defaultValue: '',
		description: 'Custom message for login page',
		displayPath: `${dpRoot}|Custom Message`,
		displayType: 'Content',
		htmlAllowed: true,
		feature: HubFeature.CandidateAccess,
		portal: 'CP',
	},
	CPLoginCompanyLogo: {
		defaultValue: 'Company Logo',
		description: 'Accessibility image alt for the company logo on the login page.',
		displayPath: `${dpRoot}|Company Logo`,
		displayType: 'Content',
		legacyName: 'CandidateAccess.Login/_View.Login.ImageInfo.CompanyLogo',
		...sharedProps,
	},
	CPLoginPageTitle: {
		defaultValue: 'Login',
		description: 'Page title for the login page',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		legacyName: 'CandidateAccess.Login/Login.Title.PageTitle',
		...sharedProps,
	},
};

export const LOC_KEYS = MapLocKeys<LoginLoc>(loginLocs);
